import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import qrcode from 'src/assets/qr-code-image.png'
import { showAlertError } from 'src/components/alertError'
import { ModalLoader } from 'src/components/modalLoader'
import { useQrCodeStaticStepForm } from 'src/context/qrcodeStaticContext'
import { useAuth } from 'src/hooks/auth'
import {
  valueStepStaticSchema,
  type ValueStepDynamic,
} from 'src/interface/qrCodeSchema'
import { listPixKeys } from 'src/requests/transfeera/list-pix-keys'
import { api } from 'src/services/api'
import { isAxiosError } from 'src/utils/isAxiosError'
import { currencyMask } from 'src/utils/maskInput'

interface QRCodeStatistical {
  id: string
  qrcode_type: string
  status: string
  txid: string
  integration_id: string
  pix_key: {
    id: string
    key: string
    key_type: string
  }
  value: number
  withdraw: any
  additional_info: string
  emv_payload: string
  image_base64: string
  created_at: string
  updated_at: string
}

export const SelectValueStatic = () => {
  const { nextStep, setFormData } = useQrCodeStaticStepForm()
  const { account, user } = useAuth()
  const [isSubmiting, setIsSubmiting] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string>()

  const { data: keys } = useQuery({
    queryKey: ['fetchPixKeys'],
    queryFn: async () => {
      const result = await listPixKeys({
        accountId: account.virtual_account_id,
      })
      if (result) {
        setSelectedKey(result[0]?.key || undefined)
        return result
      }
    },
  })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ValueStepDynamic>({
    resolver: zodResolver(valueStepStaticSchema),
  })

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('value', currencyMask(event.target.value))
  }

  const onSubmit = async (data: ValueStepDynamic) => {
    setIsSubmiting(true)

    if (!selectedKey) showAlertError('Selecione uma chave PIX.')

    const token = localStorage.getItem('@stricv2:token')

    try {
      if (user.role === 'ADMIN_BAG') {
        const response = await api.post<QRCodeStatistical>(
          '/pix/generate/static/admin',
          {
            amount: Number(data.value.replace(',', '.')),
            pixKey: selectedKey,
            description: 'Pix Estatico',
            options: {
              show_qrcode_image: true,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        setFormData({
          hash: response.data.emv_payload,
          amount: data.value,
          image: response.data.image_base64,
        })
      } else {
        const response = await api.post<QRCodeStatistical>(
          '/pix/generate/static',
          {
            amount: Number(data.value.replace(',', '.')),
            pixKey: selectedKey,
            description: 'Pix Estatico',
            options: {
              show_qrcode_image: true,
            },
            virtual_account_id: account.virtual_account_id,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        setFormData({
          hash: response.data.emv_payload,
          amount: data.value,
          image: response.data.image_base64,
        })
      }

      nextStep()
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        const errorMessage = (error.response.data as { code: string }).code
        showAlertError(errorMessage)
        console.log('asdasdasdasd')
      } else {
        showAlertError('Ocorreu um erro inesperado.')
      }
    } finally {
      setIsSubmiting(false)
    }
  }

  if (isSubmiting) {
    return <ModalLoader />
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-center gap-5 px-4"
    >
      <div className="p-1 border-2 border-[#433F3F] rounded-[10px]">
        <img src={qrcode} alt="qrcode" className="w-64 h-64" />
      </div>
      <h2 className="text-[30px] font-bold">
        Informe o valor que deseja cobrar
      </h2>
      <input
        className="border border-[#163172] rounded-md w-[70%] text-[#404040] py-2 px-4 bg-white"
        placeholder="R$0,00"
        {...register('value')}
        onChange={handleChangeAmount}
      />
      <span>Selecione a chave PIX</span>
      <select
        onChange={(e) => {
          setSelectedKey(e.target.value)
        }}
        className="border border-[#163172] rounded-md w-[70%] text-[#404040] py-2 px-2 bg-white"
        placeholder="R$0,00"
      >
        {keys?.map((key) => (
          <>
            <option key={key.id} value={key.key}>
              {key.key}
            </option>
          </>
        ))}
      </select>
      {errors.value && (
        <p className="text-red-500 text-[15px]">{errors.value.message}</p>
      )}
      <button
        type="submit"
        className="bg-[#004A8A] rounded-md text-[15px] w-[70%] font-bold text-white py-2 px-4"
      >
        Gerar a QR code para cobrança
      </button>
    </form>
  )
}
